<script setup>
import { WdsListItem, WdsPillFloating, WdsPopup } from '@wds/components';
import shareConfig from '@/config/propertyShare';

const props = defineProps({
  shareableUrl: {
    type: String,
    required: false,
    default: '',
  },
  shareMessage: {
    type: String,
    required: false,
    default: '',
  },
  shareTitle: {
    type: String,
    required: false,
    default: '',
  },
  popupHeading: {
    type: String,
    required: false,
    default: '',
  },
});

const emit = defineEmits(['shareOpen', 'shareMethodSelect']);

const { t } = useI18n();
const { isBelowMediumScreen } = useMq();
const sharePopup = ref(false);
const route = useRoute();
const { useUrl } = useUrls();
const runtimeConfig = useRuntimeConfig();
const envData = runtimeConfig?.public;

const urlParams = route?.params || null;

const shareOptions = computed(() =>
  shareConfig.shareOptions.map((item) => ({
    ...item,
    label: t(item.label),
  })),
);

const resolvedShareMessage = computed(() => {
  if (props.shareMessage) {
    return props.shareMessage;
  }
  return t('t_LOOKTHISPROPERTY', { PROPERTYNAME: urlParams.propertyName });
});

const resolvedShareableUrl = computed(() => {
  if (props.shareableUrl) {
    return props.shareableUrl;
  }
  return `${useUrl.getStaticBaseURL()}${urlParams.propertyType}/p/${urlParams.propertyId}/${urlParams.propertyName}/`;
});

const resolvedShareTitle = computed(() => {
  if (props.shareTitle) {
    return props.shareTitle;
  }
  return 'Hostelworld';
});

const resolvedPopupHeading = computed(() => {
  if (props.popupHeading) {
    return props.popupHeading;
  }
  return t('t_SHARETHISHOSTEL');
});

async function shareButtonClick() {
  emit('shareOpen');

  const shareData = {
    title: resolvedShareTitle.value,
    text: resolvedShareMessage.value,
    url: resolvedShareableUrl.value,
  };

  if (isBelowMediumScreen.value && navigator.share) {
    try {
      await navigator.share(shareData);
      shareMethodTracking('native');
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      console.error('### Share API error:', err);
    }
    return;
  }
  sharePopup.value = true;
}

function closeSharePopup() {
  sharePopup.value = false;
}

function buildShareUrlWithUtm(utmMedium) {
  const campaignId = urlParams.propertyId ?? 'events';
  const campaignSource = urlParams.propertyId ? 'shared_property' : 'shared_events';
  const campaignName = urlParams.propertyId ? `web_shared_property-${String(campaignId)}` : 'shared_events';
  const params = {
    utm_source: campaignSource,
    utm_medium: utmMedium,
    utm_campaign: String(campaignId),
    source: campaignName,
  };
  return useUrl.buildUrlQuery(resolvedShareableUrl.value, params);
}

function copyLink() {
  const url = buildShareUrlWithUtm('clipboard');

  nextTick(() => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        // @TODO check later what to do with this as we need new designs for the TOAST
        // $store.commit('toast/show', {
        //   message: $t('t_SUCCESS'),
        //   type: 'success',
        // });
      })
      .catch((error) => {
        console.error('### Error copying to clipboard:', error);
      });
  });
}

function sendEmail() {
  const url = buildShareUrlWithUtm('email');
  const emailUrl = `mailto:?body=${encodeURIComponent(url)}&subject=${encodeURIComponent(resolvedShareMessage.value)}`;

  window.open(emailUrl);
}

function shareWhatsapp() {
  const shareUrl = buildShareUrlWithUtm('whatsapp');
  const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareUrl)}`;
  window.open(url);
}

function shareMessenger() {
  const appId = envData.FACEBOOK_APP_ID;
  const urlString = buildShareUrlWithUtm('fb_messenger');
  const url = isBelowMediumScreen.value
    ? `fb-messenger://share/?link=${encodeURIComponent(urlString)}&app_id=${appId}`
    : `http://www.facebook.com/dialog/send?app_id=${appId}&link=${encodeURIComponent(
      urlString,
    )}&redirect_uri=${encodeURIComponent(urlString)}`;

  window.open(url);
}

// Options are: clipboard | email | whatsapp | fb_messenger | native (when on native WebShare API)
function shareMethodTracking(option) {
  emit('shareMethodSelect', option);
}

function handleShareOptionClick(option) {
  shareMethodTracking(option);

  switch (option) {
    case 'clipboard':
      copyLink();
      break;
    case 'email':
      sendEmail();
      break;
    case 'whatsapp':
      shareWhatsapp();
      break;
    case 'fb_messenger':
      shareMessenger();
      break;
  }
}

// @TODO - tracking on mounted
</script>

<template>
  <div class="share-button-container">
    <WdsPillFloating
      class="share-button"
      icon-start="share"
      :title="resolvedPopupHeading"
      :aria-label="resolvedPopupHeading"
      @click="shareButtonClick"
    />
    <WdsPopup
      v-if="sharePopup"
      is-open
      :heading="resolvedPopupHeading"
      close-button
      :apply-sheet="isBelowMediumScreen"
      @popup-close="closeSharePopup"
    >
      <template #popupContent>
        <div class="share-options">
          <WdsListItem
            v-for="item in shareOptions"
            :key="item.value"
            :label="item.label"
            :icon="item.icon"
            @item-selected="handleShareOptionClick(item.value)"
          />
        </div>
      </template>
    </WdsPopup>
  </div>
</template>

<style lang="scss" scoped>
.share-button {
  z-index: $wds-z-index-toast;
}

.share-options {
  margin-bottom: $wds-spacing-l;
}
</style>
